import { useState } from "react";
import { doc, setDoc, arrayUnion, updateDoc } from "firebase/firestore";
import { auth, db } from "../../Services/firebase-config";

// ─── Dynamic Field Renderer ───────────────────────────────────────────────────
const FormField = ({ field, value, onChange }) => {
    const baseInput =
        "w-full text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-custom-green-1/30 focus:border-custom-green-1 transition-all duration-200 placeholder:text-gray-400";

    switch (field.type) {
        case "text":
        case "url":
        case "email":
        case "number":
            return (
                <input
                    type={field.type}
                    placeholder={field.placeholder || ""}
                    value={value || ""}
                    onChange={(e) => onChange(field.id, e.target.value)}
                    required={field.required}
                    className={baseInput}
                />
            );

        case "textarea":
            return (
                <div className="relative">
                    <textarea
                        placeholder={field.placeholder || ""}
                        value={value || ""}
                        onChange={(e) => onChange(field.id, e.target.value)}
                        required={field.required}
                        rows={field.rows || 4}
                        maxLength={field.maxLength}
                        className={`${baseInput} resize-none`}
                    />
                    {field.maxLength && (
                        <span className="absolute bottom-2.5 right-3 text-[10px] text-gray-400">
                            {(value || "").length}/{field.maxLength}
                        </span>
                    )}
                </div>
            );

        case "select":
            return (
                <select
                    value={value || ""}
                    onChange={(e) => onChange(field.id, e.target.value)}
                    required={field.required}
                    className={`${baseInput} cursor-pointer`}
                >
                    <option value="" disabled>
                        {field.placeholder || "Select an option"}
                    </option>
                    {field.options?.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
            );

        case "radio":
            return (
                <div className="flex flex-col gap-2">
                    {field.options?.map((opt) => (
                        <label
                            key={opt.value}
                            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border cursor-pointer transition-all duration-150 text-sm ${value === opt.value
                                ? "border-custom-green-1 bg-custom-green-1/5 text-gray-800"
                                : "border-gray-200 bg-gray-50 text-gray-600 hover:border-custom-green-1/40"
                                }`}
                        >
                            <span
                                className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${value === opt.value
                                    ? "border-custom-green-1"
                                    : "border-gray-300"
                                    }`}
                            >
                                {value === opt.value && (
                                    <span className="w-2 h-2 rounded-full bg-custom-green-1" />
                                )}
                            </span>
                            <input
                                type="radio"
                                name={field.id}
                                value={opt.value}
                                checked={value === opt.value}
                                onChange={() => onChange(field.id, opt.value)}
                                className="sr-only"
                            />
                            {opt.label}
                        </label>
                    ))}
                </div>
            );

        case "checkbox":
            // Single boolean checkbox
            return (
                <label className="flex items-start gap-3 cursor-pointer group">
                    <span
                        className={`mt-0.5 w-5 h-5 flex-shrink-0 rounded-md border-2 flex items-center justify-center transition-all duration-150 ${value
                            ? "bg-custom-green-1 border-custom-green-1"
                            : "border-gray-300 group-hover:border-custom-green-1/50"
                            }`}
                    >
                        {value && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                        )}
                    </span>
                    <input
                        type="checkbox"
                        checked={!!value}
                        onChange={(e) => onChange(field.id, e.target.checked)}
                        className="sr-only"
                    />
                    <span className="text-sm text-gray-600 leading-snug">
                        {field.checkboxLabel || field.label}
                    </span>
                </label>
            );

        case "checkbox_group":
            // Multi-select checkboxes
            return (
                <div className="flex flex-col gap-2">
                    {field.options?.map((opt) => {
                        const selected = Array.isArray(value) && value.includes(opt.value);
                        return (
                            <label
                                key={opt.value}
                                className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border cursor-pointer transition-all duration-150 text-sm ${selected
                                    ? "border-custom-green-1 bg-custom-green-1/5 text-gray-800"
                                    : "border-gray-200 bg-gray-50 text-gray-600 hover:border-custom-green-1/40"
                                    }`}
                            >
                                <span
                                    className={`w-4 h-4 flex-shrink-0 rounded-md border-2 flex items-center justify-center transition-colors ${selected ? "bg-custom-green-1 border-custom-green-1" : "border-gray-300"
                                        }`}
                                >
                                    {selected && (
                                        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                </span>
                                <input
                                    type="checkbox"
                                    value={opt.value}
                                    checked={selected}
                                    onChange={() => {
                                        const current = Array.isArray(value) ? value : [];
                                        const updated = selected
                                            ? current.filter((v) => v !== opt.value)
                                            : [...current, opt.value];
                                        onChange(field.id, updated);
                                    }}
                                    className="sr-only"
                                />
                                {opt.label}
                            </label>
                        );
                    })}
                </div>
            );

        default:
            return null;
    }
};

// ─── Section Header ───────────────────────────────────────────────────────────
const SectionHeader = ({ section }) => (
    <div className="flex items-center gap-3 mb-4">
        {section.emoji && <span className="text-lg">{section.emoji}</span>}
        <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-custom-green-1">
                {section.sectionLabel}
            </p>
            <h3 className="text-sm font-semibold text-gray-800">{section.title}</h3>
        </div>
    </div>
);

// ─── Main Modal ───────────────────────────────────────────────────────────────
export default function ParticipationFormModal({ isOpen, event, onClose, onSuccess }) {
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    if (!isOpen || !event) return null;

    const participationForm = event.participationForm || { sections: [] };
    const user = auth.currentUser;

    const handleChange = (fieldId, value) => {
        setFormData((prev) => ({ ...prev, [fieldId]: value }));
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);

        try {
            const kycDocId = `${event.id}_${user.uid}`;

            await Promise.all([
                setDoc(doc(db, "kyc", kycDocId), {
                    eventId: event.id,
                    eventTitle: event.title,
                    userId: user.uid,
                    userEmail: user.email,
                    responses: formData,
                    submittedAt: new Date().toISOString(),
                }),

                updateDoc(doc(db, "events", event.id), {
                    participantIds: arrayUnion(user.uid)
                })
            ]);

            onSuccess?.();
            onClose();
        } catch (err) {
            console.error("Error submitting form:", err);
            setError("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Basic required-field validation
    const allFields = participationForm?.flatMap((s) => s.fields) || [];
    const requiredFields = allFields.filter((f) => f.required);
    const isValid = requiredFields.every((f) => {
        const val = formData[f.id];
        if (f.type === "checkbox") return val === true;
        if (Array.isArray(val)) return val.length > 0;
        return val !== undefined && val !== "" && val !== null;
    });

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative z-10 w-full max-w-3xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">

                {/* ── Header ─────────────────────────────────────────────────── */}
                <div className="flex items-start justify-between p-6 border-b border-gray-100 flex-shrink-0">
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-custom-green-1 mb-1">
                            Participation Application Form
                        </p>

                        <p className="text-xs text-gray-400 mt-1">To participate in this event, please fill out the form below.</p>

                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 ml-4"
                    >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* ── Scrollable form body ────────────────────────────────────── */}
                <div className="overflow-y-auto flex-1 p-6 space-y-8">
                    {participationForm?.map((section, sIdx) => (
                        <div key={sIdx}>
                            <SectionHeader section={section} />
                            <div className="space-y-5">
                                {section.fields?.map((field) => (
                                    <div key={field.id}>
                                        {field.type !== "checkbox" && (
                                            <label className="block text-xs font-medium text-gray-600 mb-1.5">
                                                {field.label}
                                                {field.required && (
                                                    <span className="text-red-400 ml-1">*</span>
                                                )}
                                                {field.hint && (
                                                    <span className="font-normal text-gray-400 ml-1.5">
                                                        — {field.hint}
                                                    </span>
                                                )}
                                            </label>
                                        )}
                                        <FormField
                                            field={field}
                                            value={formData[field.id]}
                                            onChange={handleChange}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* ── Footer ─────────────────────────────────────────────────── */}
                <div className="p-6 border-t border-gray-100 flex-shrink-0 flex items-center justify-between gap-4">
                    {error && <p className="text-xs text-red-500 flex-1">{error}</p>}
                    {!error && <span />}
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onClose}
                            className="text-sm font-medium text-gray-400 hover:text-gray-600 px-4 py-2 rounded-xl hover:bg-gray-50 transition-all"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={!isValid || loading}
                            className="flex items-center gap-2 text-sm font-semibold bg-custom-green-1 text-white px-5 py-2.5 rounded-xl hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 shadow-sm shadow-custom-green-1/20"
                        >
                            {loading ? (
                                <>
                                    <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                                    </svg>
                                    Submitting...
                                </>
                            ) : (
                                "Submit Application"
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}